import { ChangeEvent } from 'react';
import { X, Camera } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

interface PANVerificationProps {
  panNumber: string;
  onPanNumberChange: (value: string) => void;
  onDocumentFrontUpload: (file: File) => void;
  documentFrontPreview?: string;
  documentFrontFile?: File;
  onRemoveDocumentFront: () => void;
  errors?: {
    panNumber?: string;
    nameAsPerPan?: string;
    panDocumentFront?: string;
  };
}

const PANVerification = ({
  panNumber,
  onPanNumberChange,
  onDocumentFrontUpload,
  documentFrontPreview,
  documentFrontFile,
  onRemoveDocumentFront,
  errors,
}: PANVerificationProps) => {
  const handlePanNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .slice(0, 10);
    onPanNumberChange(value);
  };

  const handleFrontDocumentChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onDocumentFrontUpload(file);
      e.target.value = '';
    }
  };

  const handleRemoveDocument = () => {
    onRemoveDocumentFront();
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="panNumber">PAN Number</Label>
        <Input
          id="panNumber"
          placeholder="ABCDE1234F"
          value={panNumber}
          onChange={handlePanNumberChange}
          autoComplete="off"
          maxLength={10}
          className={errors?.panNumber ? 'border-red-500' : ''}
        />
        {errors?.panNumber && (
          <p className="text-sm text-destructive">{errors.panNumber}</p>
        )}
      </div>
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="panDocumentFront">PAN Card - Front</Label>
          <div className="border-2 border-dashed border-border rounded-lg p-4">
            <Input
              id="panDocumentFront"
              type="file"
              accept="image/jpeg,image/jpg,image/png"
              className="hidden width-auto"
              onChange={handleFrontDocumentChange}
            />
            <label htmlFor="panDocumentFront" className="cursor-pointer">
              <div className="flex flex-col items-center space-y-2">
                {documentFrontPreview ? (
                  <div className="relative inline-block">
                    <img
                      src={documentFrontPreview}
                      alt="PAN Card Front"
                      className="max-w-full max-h-20 object-contain rounded-lg"
                      onError={e => {
                        console.error('Image load error:', e);
                        e.currentTarget.style.display = 'none';
                      }}
                      onLoad={e => {
                        e.currentTarget.style.display = 'block';
                      }}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={e => {
                        e.preventDefault();
                        handleRemoveDocument();
                      }}
                      className="absolute top-0 right-0 p-1"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <Camera className="h-8 w-8 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      {documentFrontFile ? documentFrontFile.name : ''}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      PNG, JPG, JPEG up to 10MB
                    </span>
                  </>
                )}
              </div>
            </label>
          </div>

          {errors?.panDocumentFront && !documentFrontFile && (
            <p className="text-sm text-destructive">
              {errors.panDocumentFront}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PANVerification;
